import React from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { CotStep } from '@/api/messages';

interface ChainOfThoughtProps {
  steps: CotStep[];
  isExpanded: boolean;
  onToggleExpanded: () => void;
  isComplete: boolean;
}

const ChainOfThought: React.FC<ChainOfThoughtProps> = ({
  steps,
  isExpanded,
  onToggleExpanded,
  isComplete,
}) => {
  // Don't render if there are no steps
  if (steps.length === 0) {
    return null;
  }

  // Get status icon for each step
  const getStatusIcon = (status: CotStep['status']) => {
    switch (status) {
      case 'pending':
        return <PendingIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
      case 'in_progress':
        return <CircularProgress size={16} thickness={4} />;
      case 'completed':
        return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
      case 'error':
        return <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />;
      default:
        return <PendingIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
    }
  };

  // Get the summary text for collapsed state
  const getSummaryText = (): string => {
    if (isComplete) {
      const completedCount = steps.filter(s => s.status === 'completed').length;
      return `Completed ${completedCount} step${completedCount !== 1 ? 's' : ''}`;
    }
    
    // Find the current in-progress step
    const inProgressStep = steps.find(s => s.status === 'in_progress');
    if (inProgressStep) {
      return inProgressStep.title;
    }
    
    // Fallback to the last step
    const lastStep = steps[steps.length - 1];
    return lastStep?.title || 'Processing...';
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        backgroundColor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header - always visible */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        onClick={onToggleExpanded}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          {/* Show spinner when not complete, checkmark when complete */}
          {!isComplete ? (
            <CircularProgress size={16} thickness={4} />
          ) : (
            <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
          )}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getSummaryText()}
          </Typography>
        </Box>
        <IconButton size="small" sx={{ ml: 1 }}>
          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Expandable content */}
      <Collapse in={isExpanded}>
        <List dense sx={{ py: 0, px: 1, pb: 1 }}>
          {steps.map((step) => (
            <ListItem
              key={step.step_id}
              sx={{
                py: 0.5,
                px: 1,
                borderRadius: 1,
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {getStatusIcon(step.status)}
              </ListItemIcon>
              <ListItemText
                primary={step.title}
                secondary={step.description}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: {
                    fontWeight: step.status === 'in_progress' ? 500 : 400,
                  },
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  component: 'div',
                  sx: { 
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap',
                    mt: 0.5,
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Paper>
  );
};

export default ChainOfThought;
